
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageUpload } from '@/components/ui/image-upload';

const SPORTS_OPTIONS = [
  'Baseball',
  'Basketball', 
  'Cross Country',
  'Football',
  'Golf',
  'Gymnastics',
  'Ice Hockey',
  'Lacrosse',
  'Soccer',
  'Softball',
  'Swimming',
  'Tennis',
  'Track and Field',
  'Volleyball',
  'Water Polo',
  'Wrestling',
  'Badminton',
  'Bowling',
  'Cheerleading',
  'Dance',
  'Field Hockey',
  'Rowing',
  'Rugby',
  'Skiing',
  'Table Tennis'
];

type SportType = 'Baseball' | 'Basketball' | 'Cross Country' | 'Football' | 'Golf' | 'Gymnastics' | 'Ice Hockey' | 'Lacrosse' | 'Soccer' | 'Softball' | 'Swimming' | 'Tennis' | 'Track and Field' | 'Volleyball' | 'Water Polo' | 'Wrestling' | 'Badminton' | 'Bowling' | 'Cheerleading' | 'Dance' | 'Field Hockey' | 'Rowing' | 'Rugby' | 'Skiing' | 'Table Tennis';

interface ClubFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  clubName: string;
  setClubName: (name: string) => void;
  clubLocation: string;
  setClubLocation: (location: string) => void;
  clubWebsite: string;
  setClubWebsite: (website: string) => void;
  clubProfilePicture: string;
  setClubProfilePicture: (url: string) => void;
  clubSport: SportType | '';
  setClubSport: (sport: SportType | '') => void;
  isSubmitting: boolean;
  isEditing?: boolean;
}

export const ClubForm = ({
  isOpen,
  onOpenChange,
  onSubmit,
  clubName,
  setClubName,
  clubLocation,
  setClubLocation,
  clubWebsite,
  setClubWebsite,
  clubProfilePicture,
  setClubProfilePicture,
  clubSport,
  setClubSport,
  isSubmitting,
  isEditing = false
}: ClubFormProps) => {
  const handleImageUploaded = (url: string) => {
    setClubProfilePicture(url);
  };

  const handleImageRemoved = () => {
    setClubProfilePicture('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Club' : 'Create New Club'}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the club details.' : 'Enter the details for the new club.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="space-y-4 py-4">
            <ImageUpload
              currentImageUrl={clubProfilePicture}
              onImageUploaded={handleImageUploaded}
              onImageRemoved={handleImageRemoved}
              label="Club Profile Picture"
              bucketName="profile-images"
            />
            
            <div className="space-y-2">
              <Label htmlFor="club-name">Club Name</Label>
              <Input
                id="club-name"
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                placeholder="Enter club name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="club-sport">Sport</Label>
              <Select value={clubSport} onValueChange={setClubSport}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a sport" />
                </SelectTrigger>
                <SelectContent>
                  {SPORTS_OPTIONS.map((sport) => (
                    <SelectItem key={sport} value={sport}>
                      {sport}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="club-location">Location (optional)</Label>
              <Input
                id="club-location"
                value={clubLocation}
                onChange={(e) => setClubLocation(e.target.value)}
                placeholder="Enter club location"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="club-website">Website URL (optional)</Label>
              <Input
                id="club-website"
                type="url"
                value={clubWebsite}
                onChange={(e) => setClubWebsite(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
